import { Link, useMatchRoute } from '@tanstack/react-router'
import { ExternalLinkIcon, PanelLeftIcon, ShieldIcon } from '@/components/icons'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { ThemeToggle } from '@/components/theme-toggle'
import { env } from '@/lib/env'
import { cn } from '@/lib/utils'
import { visibleNavGroups } from '@/features/shared/lib/admin-nav'
import { AdminAccountMenu, type AdminSessionUser } from './admin-account-menu'

/**
 * Ops sidebar — coss SidebarGroup sections from ADMIN_NAV.
 * Active location via isActive / aria on SidebarMenuButton.
 */
export function AdminSidebar({
  isAdmin,
  user,
}: {
  isAdmin: boolean
  user: AdminSessionUser
}) {
  const matchRoute = useMatchRoute()
  const { state, toggleSidebar, isMobile, setOpenMobile } = useSidebar()
  const collapsed = state === 'collapsed'
  const iconOnly = collapsed && !isMobile

  const groups = visibleNavGroups(isAdmin)

  return (
    <Sidebar collapsible="icon" variant="sidebar">
      <SidebarHeader
        className={cn(
          'flex flex-row items-center gap-1',
          collapsed && !isMobile && 'flex-col gap-2 px-1',
        )}
      >
        <div
          className={cn(
            'flex min-w-0 flex-1 items-center gap-2.5 px-1',
            collapsed && !isMobile && 'flex-none justify-center px-0',
          )}
        >
          <ShieldIcon
            className="size-5 shrink-0 text-sidebar-primary"
            aria-hidden
          />
          <div className={cn('min-w-0', collapsed && !isMobile && 'sr-only')}>
            <p className="truncate font-display text-base font-semibold tracking-tight">
              Harmoni Ops
            </p>
            <p className="truncate text-[10px] text-muted-foreground">
              Staff console
            </p>
          </div>
        </div>
        {!isMobile ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 shrink-0"
            onClick={toggleSidebar}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
            title={collapsed ? 'Expand (⌘B)' : 'Collapse (⌘B)'}
          >
            <PanelLeftIcon />
          </Button>
        ) : null}
      </SidebarHeader>

      <div className={cn('px-3 pb-2', collapsed && !isMobile && 'px-1.5')}>
        <Badge
          variant={isAdmin ? 'default' : 'secondary'}
          size="sm"
          className={cn(collapsed && !isMobile && 'w-full justify-center px-0')}
        >
          {collapsed && !isMobile
            ? isAdmin
              ? 'A'
              : 'M'
            : isAdmin
              ? 'admin'
              : 'moderator'}
        </Badge>
      </div>

      <SidebarContent>
        <nav aria-label="Staff modules">
          {groups.map((group) => (
            <SidebarGroup
              key={group.id}
              className={cn(iconOnly && 'p-1.5')}
              aria-labelledby={`admin-nav-${group.id}`}
            >
              <SidebarGroupLabel id={`admin-nav-${group.id}`}>
                {group.label}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {group.items.map(({ to, label, icon: Icon, exact }) => {
                    const active = Boolean(matchRoute({ to, fuzzy: !exact }))
                    return (
                      <SidebarMenuItem key={to}>
                        <SidebarMenuButton
                          isActive={active}
                          tooltip={label}
                          render={
                            <Link
                              to={to}
                              onClick={() => {
                                if (isMobile) setOpenMobile(false)
                              }}
                            />
                          }
                        >
                          <Icon />
                          <span>{label}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </nav>
      </SidebarContent>

      <SidebarFooter className={cn('gap-2', iconOnly && 'items-center')}>
        <AdminAccountMenu user={user} collapsed={iconOnly} />
        <div
          className={cn(
            'flex w-full items-center gap-2',
            iconOnly ? 'flex-col' : 'justify-between',
          )}
        >
          <ThemeToggle />
          <Button
            type="button"
            variant="outline"
            size={iconOnly ? 'icon' : 'sm'}
            className={cn(!iconOnly && 'flex-1')}
            render={
              <a href={env.productUrl} target="_blank" rel="noreferrer" />
            }
            aria-label="Open product app"
          >
            {iconOnly ? (
              <ExternalLinkIcon />
            ) : (
              <>
                Product
                <ExternalLinkIcon className="size-3.5 opacity-70" />
              </>
            )}
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  )
}
